import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CalendarClock, Check, CircleCheck, UserX, X } from 'lucide-react';
import { clsx } from 'clsx';
import Collection from '@model-core/core/Collection';
import { MultiFilterSelect } from '@/components/list/MultiFilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { SearchInput } from '@/components/list/SearchInput';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import { Panel } from '@/components/Panel';
import { Pagination } from '@/components/table/Pagination';
import { PersonCell } from '@/components/table/PersonCell';
import { RowViewLink } from '@/components/table/RowViewLink';
import { SortableHeader } from '@/components/table/SortableHeader';
import { TeamTag } from '@/components/teams/TeamTag';
import { TimeSheetStatusBadge } from '@/components/timesheet/TimeSheetStatusBadge';
import type { SheetSummary } from '@/components/timesheet/ValidationDialogs';
import {
  toSheetSummary,
  useValidationDialogs,
  type ValidationAction,
} from '@/components/timesheet/useValidationDialogs';
import { Button } from '@/components/ui/button';
import { useListControls } from '@/hooks/useListControls';
import { useLoader } from '@/hooks/useLoader';
import { usePagedList } from '@/hooks/usePagedList';
import { useRelatedByIds } from '@/hooks/useRelatedByIds';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import PeriodModel from '@/models/entities/PeriodModel';
import TeamModel from '@/models/entities/TeamModel';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { listPage } from '@/models/listPage';
import { TEAM_STATUS, TIME_SHEET_SIGNATURE_TYPE } from '@/models/references/references';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, loadReferences, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import {
  buildSheetRequest,
  buildToSubmitRequest,
  DEFAULT_SHEET_FILTERS,
  DEFAULT_SHEET_SORT,
  DEFAULT_TO_SUBMIT_SORT,
  SUBMITTED_FILTER_STATUSES,
  statusesFromSearch,
  TO_SUBMIT_PAGE_SIZES,
  type SheetFilters,
  type SheetSortKey,
} from './validationQuery';
import { draftsLoader } from './toSubmitData';
import type { ResolvablePath } from '@/models/resolve';
import { TIME_SHEET_SUMMARIES } from '@/models/entities/TimeSheetSummaryModel';
import { ABSENCE_SIGNATURE_PENDING, SignatureCell } from '@/components/timesheet/SignatureCell';
import type { Signatures } from '@/components/timesheet/signatures';
import { useSignatures } from '@/hooks/useSignatures';
import { canDeclareAbsence, reviewActions, reviewerOf, type Reviewer } from '@/components/timesheet/reviewActions';

/** Employé et équipe des feuilles (la période est celle affichée) */
const SHEET_RESOLVABLES: ResolvablePath[] = [
  ['member', 'user'],
  ['teamMember', 'team'],
];
/** Employé et équipe des appartenances à soumettre */
const TEAM_MEMBER_RESOLVABLES: ResolvablePath[] = [['member', 'user'], ['team']];

interface ValidationContext {
  /** Période ouverte de la société (la plus récente), null si aucune */
  period: PeriodModel | null;
  /** Équipes de la société, archivées comprises (une feuille garde l'équipe de sa soumission) ; le manager : les siennes */
  teams: TeamModel[];
}

/** Période ouverte et équipes de la société (clé : l'id de la société) */
async function loadContext(companyId: string): Promise<ValidationContext> {
  const company = companyId;
  const [periods, teams] = await Promise.all([
    listPage(PeriodModel, { filters: { company }, order: { month: 'desc' }, page: 1, itemsPerPage: 1, partial: true }),
    new Collection(TeamModel).setFilter('company', company).setSort('name').list(),
    // Statuts de la période (ouverte ?) et des équipes (archivées), lus par leur id
    loadReferences('periodStatuses', 'teamStatuses'),
  ]);
  const latest = periods.models[0];
  const period = latest && !latest.isClosed ? latest : null;

  return { period, teams: teams.collection.models };
}

/**
 * Validation (§2.4, §2.5, §4.9, §6) sur la période ouverte de la société sélectionnée (les feuilles à valider y sont
 * toutes : une période ne se clôture qu'une fois tout validé), vue du Dirigeant (toutes les équipes) ou du manager (ses
 * équipes : l'API ne lui en donne pas d'autres ; il valide en signant, la feuille passe au Dirigeant). Actions selon le
 * rôle : reviewActions. Deux listes paginées côté API, chef de projet 2026-10-26 :
 *  - « À soumettre » (courte, 5 par page) : les employés attendus qui n'ont pas encore soumis, par leur appartenance
 *    active à une équipe (GET /team_members?toSubmit : les feuilles ne sont pas créées d'avance), sans feuille ou avec
 *    un brouillon (jamais soumis, ou refusé), plus ceux sortis de leur équipe avec un brouillon commencé (« n'est plus
 *    dans l'équipe » : il va au bout, par une absence en attendant mieux) ; équipe, total en cours, déclarer une absence
 *  - « Soumises » : les feuilles hors brouillon (GET /time_sheets, en attente, validées…), avec la date de soumission,
 *    les signatures, le statut (filtre à choix multiple : en attente du manager, du Dirigeant, validée ; Soumise et
 *    Refusée sont transitoires) ; valider ou refuser (motif) une feuille en attente
 * Les soumises en premier, « À soumettre » repliée par défaut (2026-10-29). Recherche et filtre par équipes communs aux
 * deux listes, sans filtre à l'arrivée (filtre par défaut selon le rôle à
 * décider) ; tris par nom et par équipe (et date de soumission, total pour les soumises). Chaque liste se replie (son nombre
 * reste affiché ; repli gardé par navigateur). En tête, la période et sa clôture automatique (côté API,
 * PeriodClosureHandler).
 */
export function ValidationPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const {
    data: context,
    error: contextError,
    reload: reloadContext,
  } = useLoader(company ? String(company.id) : null, loadContext);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => {
    setVersion((v) => v + 1);
    reloadContext();
  }, [reloadContext]);
  const { open, dialog } = useValidationDialogs(refresh);
  const reviewer = useMemo(() => reviewerOf(company), [company]);
  // Statuts de départ des « Soumises » (?status=…, ex. une tuile du Tableau de bord)
  const [searchParams] = useSearchParams();
  const initialStatuses = useMemo(() => statusesFromSearch(searchParams), [searchParams]);

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }
  if (!context) {
    return <ListStatus loading={!contextError} error={contextError} />;
  }
  if (!context.period) {
    return <Panel className="p-6 text-sm text-slate-500">Aucune période ouverte pour cette société.</Panel>;
  }
  const { period } = context;

  return (
    <>
      <PeriodSheets
        // Autre société : filtres et pagination remis à zéro
        key={company?.id}
        context={context}
        reviewer={reviewer}
        period={period}
        version={version}
        initialStatuses={initialStatuses}
        onAction={open}
      />

      {dialog}
    </>
  );
}

function PeriodSheets({
  context,
  reviewer,
  period,
  version,
  initialStatuses,
  onAction,
}: {
  context: ValidationContext;
  reviewer: Reviewer;
  period: PeriodModel;
  version: number;
  /** Statuts de départ des « Soumises » (lus dans l'URL) */
  initialStatuses: string[];
  onAction: (action: ValidationAction, sheet: SheetSummary) => void;
}) {
  // Recherche et équipes : communes aux deux listes (chacune revient en page 1 quand elles changent)
  const shared = useListControls({ filters: DEFAULT_SHEET_FILTERS });
  const { filters, search, changeFilters } = shared;
  // Le manager : ses équipes seulement, toutes tant qu'il n'en choisit pas
  const scope = reviewer.isDirector ? null : reviewer.managedTeamIds;
  const teams = scope ? context.teams.filter((team) => scope.includes(Number(team.id))) : context.teams;
  const scopeKey = scope?.join(',') ?? '';
  const sharedFilters = useMemo(
    () => ({ search, teams: filters.teams.length || !scopeKey ? filters.teams : scopeKey.split(',') }),
    [search, filters.teams, scopeKey],
  );
  // Statut et type des feuilles, statut des appartenances : lus par leur id dans les listes de référence (chargées une
  // fois par session)
  useReferences('timeSheetStatuses', 'timeSheetTypes', 'teamMemberStatuses');
  const filtered = !!search || filters.teams.length > 0;
  const lists = { period, filters: sharedFilters, filtered, reviewer, version, onAction };

  return (
    <div className="space-y-6">
      <Panel as="section" aria-label="Période" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-blue-600" />
            {period.monthLabel}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {scope
              ? 'Les feuilles de vos équipes : validez-les pour les transmettre au Dirigeant.'
              : 'La période se clôturera automatiquement dès que chaque employé attendu aura une feuille validée.'}
          </p>
        </div>
        <ListToolbar
          search={
            <SearchInput
              label="Rechercher un employé"
              value={filters.search}
              onChange={(value) => changeFilters({ search: value })}
            />
          }
        >
          <MultiFilterSelect
            label="Filtrer par équipe"
            allLabel="Toutes les équipes"
            noun="équipes"
            options={teams.map((team) => ({
              value: String(team.id),
              label: `${team.name}${team.teamStatusReference === TEAM_STATUS.ARCHIVED ? ' (archivée)' : ''}`,
            }))}
            value={filters.teams}
            onChange={(teams) => changeFilters({ teams })}
          />
        </ListToolbar>
      </Panel>

      {/* Les soumises d'abord : ce qui est à valider (chef de projet, 2026-10-29) */}
      <SubmittedList
        // Autres statuts dans l'URL (nouveau clic sur une tuile) : la liste repart de ceux-là
        key={initialStatuses.join(',')}
        {...lists}
        initialStatuses={initialStatuses}
      />
      <ToSubmitList {...lists} />
    </div>
  );
}

interface ListProps {
  period: PeriodModel;
  /** Filtres envoyés (le manager : ses équipes à défaut d'un choix) */
  filters: SheetFilters;
  /** Recherche ou équipes choisies (message d'une liste vide) */
  filtered: boolean;
  reviewer: Reviewer;
  version: number;
  onAction: (action: ValidationAction, sheet: SheetSummary) => void;
}

/** Revient en page 1 quand les filtres communs changent */
const filtersKey = ({ search, teams }: SheetFilters) => `${search}|${teams.join(',')}`;

/**
 * « À soumettre » : appartenance active des employés attendus sans feuille soumise, avec leur brouillon ; appartenance
 * inactive gardée par un brouillon (sorti de son équipe)
 */
function ToSubmitList({ period, filters, filtered, reviewer, version, onAction }: ListProps) {
  const list = useListControls({
    filters: {},
    sort: DEFAULT_TO_SUBMIT_SORT,
    pageSizes: TO_SUBMIT_PAGE_SIZES,
    resetKey: filtersKey(filters),
  });
  const { sort, page, itemsPerPage, changeSort } = list;
  const periodId = Number(period.id);
  const request = useMemo(
    () => buildToSubmitRequest(periodId, filters, sort, page, itemsPerPage),
    [periodId, filters, sort, page, itemsPerPage],
  );
  const {
    models: teamMembers,
    totalItems,
    loading,
    error,
  } = usePagedList(TeamMemberModel, request, version, { resolvables: TEAM_MEMBER_RESOLVABLES });
  const drafts = useRelatedByIds(
    useMemo(() => teamMembers.map((teamMember) => teamMember.memberId), [teamMembers]),
    useMemo(() => draftsLoader(periodId), [periodId]),
    version,
  );

  return (
    <CollapsiblePanel
      title="À soumettre"
      description="Employés qui n'ont pas encore signé leur feuille (pas commencée, en cours, ou refusée et à corriger)."
      count={loading && teamMembers.length === 0 ? undefined : totalItems}
      // Repliée par défaut : les soumises, au-dessus, sont ce qui est à traiter (chef de projet, 2026-10-29)
      defaultCollapsed
      data-testid="drafts-sheets"
    >
      <div className="overflow-x-auto">
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortableHeader className="px-6 py-4" label="Employé" sortKey="name" sort={sort} onSort={changeSort} />
              <SortableHeader className="px-6 py-4" label="Équipe" sortKey="team" sort={sort} onSort={changeSort} />
              <th className="px-6 py-4">Total heures</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {teamMembers.map((teamMember) => {
              const loaded = drafts?.has(teamMember.memberId) ?? false;
              const draft = drafts?.get(teamMember.memberId) ?? null;
              const { fullName } = teamMember;
              const summary: SheetSummary = {
                urn: draft?.urn ?? null,
                memberId: teamMember.memberId,
                periodId,
                fullName,
                monthLabel: period.monthLabel,
                totalMinutes: draft?.totalMinutes ?? 0,
                isAbsence: false,
                // Absence signée aussitôt : en Dirigeant, sinon en Manager (comme l'API)
                signAs: reviewer.isDirector ? TIME_SHEET_SIGNATURE_TYPE.DIRECTOR : TIME_SHEET_SIGNATURE_TYPE.MANAGER,
              };
              const left = !teamMember.isActive;

              return (
                <tr key={teamMember.id} className="hover:bg-slate-50/80 transition-colors" data-testid="sheet-row">
                  <PersonCell
                    to={draft ? `/validation/${draft.id}` : undefined}
                    firstName={teamMember.firstName}
                    lastName={teamMember.lastName}
                    fullName={fullName}
                    suffix={left ? "n'est plus dans l'équipe" : undefined}
                  />
                  <td className="px-6 py-4">
                    <TeamTag name={teamMember.teamName} color={teamMember.teamColor} />
                  </td>
                  <td className="px-6 py-4 font-semibold text-slate-900 whitespace-nowrap">
                    {draft ? formatMinutes(draft.totalMinutes) : <span className="font-normal text-slate-400">–</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {draft && <RowViewLink to={`/validation/${draft.id}`} label={`Voir la feuille de ${fullName}`} />}
                      {canDeclareAbsence(reviewer, teamMember.memberId, teamMember.teamId) && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!loaded}
                          onClick={() => onAction('absence', summary)}
                        >
                          <UserX />
                          Déclarer une absence
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={loading && teamMembers.length === 0}
          error={error}
          empty={
            !loading &&
            teamMembers.length === 0 &&
            (filtered
              ? 'Aucun employé ne correspond à ces critères.'
              : 'Tous les employés attendus ont soumis leur feuille.')
          }
        />
      </div>

      <Pagination {...list.pagination} totalItems={totalItems} label={['employé', 'employés']} />
    </CollapsiblePanel>
  );
}

/** « Soumises » : feuilles hors brouillon ; initialStatuses : filtre de départ (lu dans l'URL) */
function SubmittedList({
  period,
  filters,
  filtered,
  reviewer,
  version,
  onAction,
  initialStatuses,
}: ListProps & { initialStatuses: string[] }) {
  const list = useListControls({
    // À l'arrivée : celles qui l'attendent ; des statuts dans l'URL (tuile du Tableau de bord) : toutes, sur ces statuts
    filters: { statuses: initialStatuses, awaitingMe: initialStatuses.length === 0 },
    sort: DEFAULT_SHEET_SORT,
    resetKey: filtersKey(filters),
  });
  const { sort, page, itemsPerPage, changeFilters, changeSort } = list;
  const { statuses, awaitingMe } = list.filters;
  const statusOptions = useReferenceOptions('timeSheetStatuses');
  const periodId = Number(period.id);
  const request = useMemo(
    () =>
      buildSheetRequest(
        periodId,
        filters,
        statuses,
        sort,
        page,
        itemsPerPage,
        (reference) => findReferenceId(statusOptions, reference),
        awaitingMe,
      ),
    [periodId, filters, statuses, sort, page, itemsPerPage, statusOptions, awaitingMe],
  );
  const {
    models: sheets,
    totalItems,
    loading,
    error,
  } = usePagedList(TimeSheetModel, request, version, {
    resolvables: SHEET_RESOLVABLES,
    summary: TIME_SHEET_SUMMARIES,
  });
  const signatures = useSignatures(
    useMemo(() => sheets.map((sheet) => Number(sheet.id)), [sheets]),
    version,
  );
  const header = (label: string, sortKey: SheetSortKey) => (
    <SortableHeader className="px-6 py-4" label={label} sortKey={sortKey} sort={sort} onSort={changeSort} />
  );

  return (
    <CollapsiblePanel
      title="Soumises"
      description="Feuilles signées par leur employé (ou absences déclarées) : à valider, ou déjà validées."
      count={loading && sheets.length === 0 ? undefined : totalItems}
      data-testid="submitted-sheets"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <AwaitingMeToggle value={awaitingMe} onChange={(awaitingMe) => changeFilters({ awaitingMe })} />
          {/* Statuts : sur « Toutes » seulement (« Qui m'attendent » est déjà une sélection) */}
          {!awaitingMe && (
            <MultiFilterSelect
              label="Filtrer par statut"
              allLabel="Tous les statuts"
              noun="statuts"
              options={statusOptions
                .filter((status) => SUBMITTED_FILTER_STATUSES.includes(status.reference))
                .map((status) => ({ value: status.reference, label: status.label }))}
              value={statuses}
              onChange={(statuses) => changeFilters({ statuses })}
            />
          )}
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {header('Employé', 'name')}
              {header('Équipe', 'team')}
              {header('Soumise le', 'submittedAt')}
              {header('Total heures', 'total')}
              <th className="px-6 py-4">Employé</th>
              <th className="px-6 py-4">Manager</th>
              <th className="px-6 py-4">Dirigeant</th>
              <th className="px-6 py-4">Statut</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sheets.map((sheet) => (
              <SheetRow
                key={sheet.id}
                sheet={sheet}
                signatures={signatures?.get(Number(sheet.id))}
                signaturesLoading={!signatures}
                reviewer={reviewer}
                onAction={(action, signAs) => onAction(action, toSheetSummary(sheet, period.monthLabel, signAs))}
              />
            ))}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={loading && sheets.length === 0}
          error={error}
          empty={
            !loading &&
            sheets.length === 0 &&
            !awaitingMe &&
            (filtered || statuses.length
              ? 'Aucune feuille ne correspond à ces critères.'
              : "Aucune feuille soumise pour l'instant.")
          }
        >
          {!loading && sheets.length === 0 && awaitingMe && (
            // « Qui m'attendent » vide : un état « à jour », et toute la liste à portée de main (rien n'a disparu)
            <div role="status" className="px-6 py-10 flex flex-col items-center text-center gap-2">
              <div className="p-3 rounded-full bg-green-50">
                <CircleCheck className="w-6 h-6 text-green-600" aria-hidden />
              </div>
              <p className="font-semibold text-slate-900">
                {filtered ? 'Rien ne vous attend pour ces critères' : 'Rien ne vous attend'}
              </p>
              <p className="text-sm text-slate-500">
                {filtered
                  ? 'Modifiez la recherche ou les équipes, ou voyez toutes les feuilles.'
                  : 'Toutes les feuilles qui vous attendaient ont été traitées.'}
              </p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => changeFilters({ awaitingMe: false })}>
                Voir toutes les feuilles
              </Button>
            </div>
          )}
        </ListStatus>
      </div>

      <Pagination {...list.pagination} totalItems={totalItems} label={['feuille', 'feuilles']} feminine />
    </CollapsiblePanel>
  );
}

function SheetRow({
  sheet,
  signatures,
  signaturesLoading,
  reviewer,
  onAction,
}: {
  sheet: TimeSheetModel;
  /** Signatures Employé, Manager, Dirigeant (comme le récapitulatif de l'Historique) */
  signatures: Signatures | undefined;
  signaturesLoading: boolean;
  reviewer: Reviewer;
  onAction: (action: ValidationAction, signAs?: SheetSummary['signAs']) => void;
}) {
  const { fullName } = sheet;
  const { validateAs, canReject } = reviewActions(reviewer, sheet);

  return (
    <tr className="hover:bg-slate-50/80 transition-colors" data-testid="sheet-row">
      <PersonCell
        to={`/validation/${sheet.id}`}
        firstName={sheet.firstName}
        lastName={sheet.lastName}
        fullName={fullName}
        suffix={sheet.isAbsence ? 'absence' : undefined}
      />
      <td className="px-6 py-4">
        <TeamTag name={sheet.teamName} color={sheet.teamColor} />
      </td>
      {/* Dernière soumission ; pour une absence, sa déclaration */}
      <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
        {sheet.submittedAt?.format(DATE_TIME_FORMAT) ?? '–'}
      </td>
      <td className="px-6 py-4 font-semibold text-slate-900 whitespace-nowrap">
        {sheet.totalMinutes || sheet.isAbsence ? (
          formatMinutes(sheet.totalMinutes)
        ) : (
          <span className="font-normal text-slate-400 italic">0h (aucune heure)</span>
        )}
      </td>
      <td className="px-6 py-4">
        {/* Absence : validée sans l'employé, qui la signe ensuite */}
        <SignatureCell
          signature={signatures?.employee}
          loading={signaturesLoading}
          pending={sheet.isAbsence ? ABSENCE_SIGNATURE_PENDING : undefined}
        />
      </td>
      <td className="px-6 py-4">
        <SignatureCell signature={signatures?.manager} loading={signaturesLoading} />
      </td>
      <td className="px-6 py-4">
        <SignatureCell signature={signatures?.director} loading={signaturesLoading} />
      </td>
      <td className="px-6 py-4">
        <TimeSheetStatusBadge reference={sheet.timeSheetStatusReference} label={sheet.timeSheetStatusLabel} />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <RowViewLink to={`/validation/${sheet.id}`} label={`Voir la feuille de ${fullName}`} />
          {validateAs && (
            <Button
              size="icon"
              variant="ghost"
              className="text-green-700 hover:bg-green-50 hover:text-green-800"
              aria-label={`Valider la feuille de ${fullName}`}
              title="Valider"
              onClick={() => onAction('validate', validateAs)}
            >
              <Check />
            </Button>
          )}
          {canReject && (
            <Button
              size="icon"
              variant="ghost"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              aria-label={sheet.isAbsence ? `Annuler l'absence de ${fullName}` : `Refuser la feuille de ${fullName}`}
              title={sheet.isAbsence ? "Annuler l'absence" : 'Refuser'}
              onClick={() => onAction('reject')}
            >
              <X />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** « Qui m'attendent | Toutes » : les feuilles qui attendent l'utilisateur (à l'arrivée), ou toutes les soumises */
function AwaitingMeToggle({ value, onChange }: { value: boolean; onChange: (awaitingMe: boolean) => void }) {
  const option = (awaitingMe: boolean, label: string) => (
    <button
      type="button"
      aria-pressed={value === awaitingMe}
      onClick={() => onChange(awaitingMe)}
      className={clsx(
        'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
        value === awaitingMe ? 'bg-card text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
      )}
    >
      {label}
    </button>
  );

  return (
    <div role="group" aria-label="Feuilles affichées" className="inline-flex rounded-lg bg-slate-100 p-1">
      {option(true, "Qui m'attendent")}
      {option(false, 'Toutes')}
    </div>
  );
}
